import { useMemo, useRef } from 'react';

import { useAuth } from 'context/AuthContext';
import { Carga, useCargaPorChave } from 'hooks/useCargaPorChave';
import { getColecaoDeSelos } from 'services/colecao-de-selos.service';
import { User } from 'types/user';
import { ciclosDeFechamento } from 'utils/ciclos-de-fechamento';

import { montarColecao, SeloDaColecao } from './selos-modelo';

/**
 * Uma carga por jogador e conjunto de ciclos na sessão, da foto mais recente: o Meu Perfil e a Galeria leem a mesma
 * coleção, e ir de um para o outro não repete a leitura. A carga de uma foto anterior é substituída, não acumulada.
 * Falha não fica guardada, para o "Tentar de novo" ler de novo.
 */
const cargas = new Map<string, { assinatura: string; carga: Promise<SeloDaColecao[]> }>();

function carregarColecao(colecaoDoJogador: string, assinatura: string, ciclos: string[]) {
  const guardada = cargas.get(colecaoDoJogador);
  if (guardada?.assinatura === assinatura) return guardada.carga;

  // SEL-02: as três leituras de cada ciclo vêm juntas, num aggregate só (AJH-49).
  const carga = getColecaoDeSelos(ciclos).then(montarColecao);
  const entrada = { assinatura, carga };
  cargas.set(colecaoDoJogador, entrada);
  // Só apaga a própria entrada: uma falha que chega atrasada não derruba uma carga mais nova do mesmo jogador.
  carga.catch(() => {
    if (cargas.get(colecaoDoJogador) === entrada) cargas.delete(colecaoDoJogador);
  });
  return carga;
}

/** Só para os testes: cada um começa sem carga guardada. */
export function esquecerColecoesDeSelos() {
  cargas.clear();
  gravacoesDaFoto = 0;
}

/**
 * Quantas vezes o editor de foto gravou nesta sessão. Gravar sem trocar a foto mantém a mesma URL, mas os triggers
 * regeram as molduras do mesmo jeito (inclusive as que faltavam): a gravação também pede a coleção de novo.
 */
let gravacoesDaFoto = 0;

/** O editor de foto avisa que gravou, depois do PUT (os triggers de `player` rodam dentro dele). */
export function avisarFotoGravada() {
  gravacoesDaFoto += 1;
}

/** Funci com foto enviada (`avatar_type === 1`): só assim há moldura (o selo com a foto). */
export function temFotoEnviada(user?: User) {
  return user?.extra?.avatar_type === 1;
}

/**
 * A foto entra na chave da carga: ao trocá-la (nos selos, no Meu Perfil ou na Home), os triggers `after_update`
 * de `player` regeram as molduras, e o `refreshUser` do editor traz a foto nova, que pede a coleção de novo (AJH-48).
 */
function assinaturaDaFoto(user: User, gravacoes: number) {
  return `${user.extra?.avatar_type ?? ''}:${user.image?.medium?.url ?? ''}:${gravacoes}`;
}

/**
 * As gravações entram na chave só junto com o usuário que o `refreshUser` traz depois delas. Um render no meio (o
 * editor fechando) ainda tem a foto anterior: lê-lo com o contador novo faria uma leitura a mais (SEL-F03).
 */
function useGravacoesDoUsuario(user?: User) {
  const vistas = useRef({ user, gravacoes: gravacoesDaFoto });
  if (vistas.current.user !== user) vistas.current = { user, gravacoes: gravacoesDaFoto };
  return vistas.current.gravacoes;
}

/** A coleção de selos do funci em todos os ciclos de fechamento, para a galeria `/selos` e o Meu Perfil. */
export function useColecaoDeSelos() {
  const { user, realCycle } = useAuth();
  const gravacoes = useGravacoesDoUsuario(user);

  // Os mesmos ciclos de Destaque e do Extrato de Premiação (gate `cycle-ceilings`).
  const ciclos = useMemo(() => ciclosDeFechamento(realCycle), [realCycle]);

  const ids = ciclos.map(ciclo => ciclo._id);
  const colecaoDoJogador = user && realCycle ? `${user._id}|${ids.join(',')}` : undefined;
  const assinatura = user ? assinaturaDaFoto(user, gravacoes) : '';
  const chave = colecaoDoJogador ? `${colecaoDoJogador}|${assinatura}` : undefined;
  const { carga, recarregar } = useCargaPorChave(chave, async () =>
    colecaoDoJogador ? carregarColecao(colecaoDoJogador, assinatura, ids) : [],
  );

  // Enquanto relê depois da troca de foto, a coleção anterior fica na tela: a lista não some, o foco volta ao
  // botão que abriu o editor e o filtro de ciclo se mantém. Se a releitura falhar, fica a coleção anterior.
  const ultimaPronta = useRef<{ colecaoDoJogador: string; carga: Carga<SeloDaColecao[]> }>();
  if (carga.estado === 'pronto' && colecaoDoJogador) ultimaPronta.current = { colecaoDoJogador, carga };
  const anterior = ultimaPronta.current?.colecaoDoJogador === colecaoDoJogador ? ultimaPronta.current?.carga : undefined;

  return { carga: carga.estado !== 'pronto' && anterior ? anterior : carga, recarregar, ciclos, temFoto: temFotoEnviada(user) };
}
